import * as React from "react"
import type { useRender } from "@base-ui/react/use-render"

/**
 * Bridges the Radix/shadcn `asChild` API onto Base UI's `render` prop.
 *
 * Base UI has no `asChild`: it composes through `render`. Keeping `asChild`
 * on our wrappers means the ~194 existing call sites stay untouched while the
 * primitives underneath are Base UI.
 *
 * Returns the `render` element to pass to Base UI plus the props to spread,
 * with `children` dropped when they were consumed as the render target.
 */
export function resolveAsChild<
  P extends { children?: React.ReactNode },
  R = useRender.RenderProp,
>(
  asChild: boolean | undefined,
  render: R | undefined,
  props: P,
): { render: R | undefined; props: P } {
  if (render) {
    return { render, props }
  }
  if (asChild && React.isValidElement(props.children)) {
    const { children: _children, ...rest } = props
    return { render: props.children as R, props: rest as P }
  }
  return { render: undefined, props }
}
